import { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  ModalProps,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { DocumentType } from '../../../../enums';
import { PostTransactionDocumentsDTO } from '../../../../api';
import FileUpload from '../../../../components/inputFile/inputFile';

const allowedDocuments = {
  [DocumentType.FINANCIAL_STATEMENT]: DocumentType.FINANCIAL_STATEMENT,
  [DocumentType.SOURCE_OF_FUNDS]: DocumentType.SOURCE_OF_FUNDS,
  [DocumentType.PROOF_OF_BANK_ACCOUNT]: DocumentType.PROOF_OF_BANK_ACCOUNT,
  [DocumentType.OTHER]: DocumentType.OTHER,
};

type UploadDocumentModalProps = {
  open: boolean;
  onClose: () => void;
  onUpload: () => void;
  fields: PostTransactionDocumentsDTO['body'] | null;
  fieldsHandler: (
    e: SelectChangeEvent | ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    isFile?: boolean
  ) => void;
  isLoading: boolean;
};

export const UploadDocumentModal: FC<UploadDocumentModalProps> = (props) => {
  const { open, onClose, onUpload, fields, fieldsHandler, isLoading } = props;
  const { t } = useTranslation();

  // validation for required fields
  const requiredFields = ['type', 'file'] as (keyof PostTransactionDocumentsDTO['body'])[];
  const checkSubmitDisabling = () => {
    if (!fields) return true;

    for (const field of requiredFields) {
      // @ts-ignore
      if (fields[field] === undefined || fields[field] === '') {
        return true;
      }
    }

    return false;
  };

  // close handlers
  const closeHandlerWithPrevent: ModalProps['onClose'] = (_, reason) => {
    if (reason === 'backdropClick') return;
    onClose();
  };
  const closeHandler = () => {
    onClose();
  };

  const documentTypeSelectOptions = Object.values(allowedDocuments).map((it) => {
    return (
      <MenuItem key={it} value={it}>
        {t(`common.document-type.${it}`)}
      </MenuItem>
    );
  });

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => closeHandlerWithPrevent(_, reason)}
      PaperProps={{ sx: { minWidth: '380px' } }}
    >
      <DialogTitle>{t('transaction.modal.upload.title')}</DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={() => closeHandler()}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Stack direction="column" spacing={2}>
          <TextField
            select
            name="type"
            value={fields?.type ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('transaction.modal.upload.document-type')}
            fullWidth
            // InputLabelProps={{ shrink: true }}
          >
            {documentTypeSelectOptions}
          </TextField>

          <FileUpload
            name="file"
            fieldsHandler={fieldsHandler}
            label={t('transaction.modal.upload.upload-file')}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={() => onUpload()}
          disabled={isLoading || checkSubmitDisabling()}
        >
          {t('transaction.modal.upload.upload-btn')}
        </Button>
        <Button variant="outlined" onClick={() => onClose()} disabled={isLoading}>
          {t('transaction.modal.upload.cancel-btn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
